import { useIntl } from '@weenat/wintl'
import { isNil } from 'lodash-es'
import { useState } from 'react'
import Button from '../kit/Button'
import DelimitedFlex from '../kit/DelimitedFlex'
import { NumberFieldPrimitive } from '../kit/fields/NumberField'
import Modal, { ModalProps } from '../kit/Modal'

interface ColdHoursParams {
  coldTemperatureThreshold: number
}

interface HeatAccumulationParams {
  base: number | undefined
  gddTMax: number | undefined
}

interface EditPeriodBasedCumulativeSumModalProps extends ModalProps {
  initialParams: ColdHoursParams | HeatAccumulationParams
  onConfirm: (params: ColdHoursParams | HeatAccumulationParams) => void
}

const EditPeriodBasedCumulativeSumModal: FC<EditPeriodBasedCumulativeSumModalProps> = ({
  isOpen,
  close,
  onConfirm,
  initialParams
}) => {
  const { t } = useIntl()

  const [heatAccParams, setHeatAccParams] = useState<HeatAccumulationParams | undefined>(
    'base' in initialParams ? initialParams : undefined
  )

  const [coldHoursParams, setColdHoursParams] = useState<ColdHoursParams | undefined>(
    'coldTemperatureThreshold' in initialParams ? initialParams : undefined
  )

  return (
    <Modal
      width={400}
      isOpen={isOpen}
      close={close}
      footer={
        <DelimitedFlex $isDelimitedOnTop $justifyContent='flex-end' $py={'md'} $px={'lg'}>
          <Button
            onPress={() => {
              if ('base' in initialParams && !isNil(heatAccParams)) {
                onConfirm(heatAccParams)
              } else if (!isNil(coldHoursParams)) {
                onConfirm(coldHoursParams)
              }
            }}
          >
            {t('actions.confirm')}
          </Button>
        </DelimitedFlex>
      }
    >
      {'base' in initialParams ? (
        <>
          <NumberFieldPrimitive
            name='min'
            label={t('models.alert.fields.cumulative_sum_dd_min_threshold.label', {
              capitalize: true
            })}
            value={heatAccParams?.base}
            onChange={(newBase) => {
              setHeatAccParams((current) => {
                if (!isNil(current)) {
                  return {
                    ...current,
                    base: newBase
                  }
                }
              })
            }}
            isClearable
          />
          <NumberFieldPrimitive
            name='max'
            label={t('models.alert.fields.cumulative_sum_dd_max_threshold.label', {
              capitalize: true
            })}
            value={heatAccParams?.gddTMax}
            onChange={(newMax) => {
              setHeatAccParams((current) => {
                if (!isNil(current)) {
                  return {
                    ...current,
                    gddTMax: newMax
                  }
                }
              })
            }}
            isClearable
          />
        </>
      ) : (
        <NumberFieldPrimitive
          name='cold-threshold'
          label={t('help.cumulative_sum_create_form_cold_threshold_label')}
          value={coldHoursParams?.coldTemperatureThreshold}
          onChange={(value) => {
            if (!isNil(value)) setColdHoursParams({ coldTemperatureThreshold: value })
          }}
        />
      )}
    </Modal>
  )
}

export default EditPeriodBasedCumulativeSumModal
